import React, { useEffect, useMemo, useState } from "react";
import { api, fmtMoney } from "../lib/api";
import { AXIS_STROKE, AXIS_LINE, GRID_STROKE, TOOLTIP_CONTENT_STYLE, TOOLTIP_LABEL_STYLE, TOOLTIP_CURSOR_PAARS, LEGEND_WRAPPER_STYLE, dollarTick, dollarTooltip } from "../lib/chartStyles";
import { BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

// Brand palette tailored for the champagne/paars/groen theme
const PALETTE = [
  "#9e8193", "#88a17e", "#bfaf9d", "#c9b896", "#d7b5c9",
  "#a86464", "#6e8765", "#dbd6d2", "#e6d0dd", "#7a8e6a",
];

export default function Ladder() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/portfolio/ladder");
        setData(data);
      } finally { setLoading(false); }
    })();
  }, []);

  const { chartData, colorMap } = useMemo(() => {
    if (!data) return { chartData: [], colorMap: {} };
    const cmap = {};
    data.tickers.forEach((t, i) => { cmap[t] = PALETTE[i % PALETTE.length]; });
    const cd = data.rungs.map((r) => ({
      year: r.year,
      label: `${r.year}${r.years_out > 0 ? ` · +${r.years_out}y` : " · NOW"}`,
      ...r.tickers,
    }));
    return { chartData: cd, colorMap: cmap };
  }, [data]);

  const totals = useMemo(() => {
    if (!data) return { income: 0, principal: 0, maxYear: null, minYear: null };
    const income = data.rungs.reduce((s, r) => s + r.total_income, 0);
    const principal = data.rungs.reduce((s, r) => s + r.principal_returning, 0);
    const years = data.rungs.map((r) => r.year);
    return { income, principal, maxYear: Math.max(...years), minYear: Math.min(...years) };
  }, [data]);

  if (loading) return <div className="min-h-screen bg-champagne p-8 text-graphite/55 font-mono text-sm animate-pulse">Computing ladder...</div>;

  return (
    <div className="min-h-screen bg-champagne">
      <div className="border-b border-stone px-8 py-5 sticky top-0 bg-champagne z-10 flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-paars-dark font-bold">// SYS::LADDER</div>
          <h1 className="text-2xl font-bold tracking-tight text-graphite">Maturity Yield Ladder</h1>
        </div>
        <BarChart3 className="w-5 h-5 text-paars-dark" strokeWidth={1.5} />
      </div>

      <div className="p-8 space-y-6">
        {/* Summary stats */}
        <div className="grid grid-cols-4 gap-px bg-stone border border-stone">
          <Stat label="Maturity Years" value={data?.rungs.length || 0} sub={data?.rungs.length ? `${totals.minYear} → ${totals.maxYear}` : "—"} />
          <Stat label="Total Annual Income" value={fmtMoney(totals.income)} sub="Across all rungs" accent="text-groen-dark" />
          <Stat label="Principal Returning" value={fmtMoney(totals.principal)} sub="At full maturity" accent="text-paars-dark" />
          <Stat label="Issuers" value={data?.tickers.length || 0} sub="Unique tickers" />
        </div>

        {/* Stacked bar chart */}
        <div className="border border-stone bg-champagne p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-graphite/55 font-bold">// COUPON INCOME BY MATURITY YEAR</div>
              <h3 className="text-base font-semibold text-graphite mt-1">Stacked by issuer · spot your laddering gaps</h3>
            </div>
          </div>
          <div className="h-96">
            {chartData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-graphite/45 text-sm font-mono">
                ADD POSITIONS TO BUILD LADDER
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid stroke={GRID_STROKE} vertical={false} />
                  <XAxis dataKey="label" stroke={AXIS_STROKE} fontSize={11} tickLine={false} axisLine={AXIS_LINE} />
                  <YAxis stroke={AXIS_STROKE} fontSize={11} tickLine={false} axisLine={AXIS_LINE} tickFormatter={dollarTick} />
                  <Tooltip
                    contentStyle={TOOLTIP_CONTENT_STYLE}
                    labelStyle={TOOLTIP_LABEL_STYLE}
                    cursor={TOOLTIP_CURSOR_PAARS}
                    formatter={dollarTooltip}
                  />
                  <Legend wrapperStyle={LEGEND_WRAPPER_STYLE} />
                  {data.tickers.map((t) => (
                    <Bar key={t} dataKey={t} stackId="ladder" fill={colorMap[t]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Detail table */}
        <div className="border border-stone bg-champagne">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-stone/50 border-b border-stone">
                <th className="px-4 py-3 text-left text-[10px] tracking-[0.2em] uppercase font-bold text-graphite/55">Year</th>
                <th className="px-4 py-3 text-right text-[10px] tracking-[0.2em] uppercase font-bold text-graphite/55">Annual Income</th>
                <th className="px-4 py-3 text-right text-[10px] tracking-[0.2em] uppercase font-bold text-graphite/55">Principal Returning</th>
                <th className="px-4 py-3 text-right text-[10px] tracking-[0.2em] uppercase font-bold text-graphite/55">Market Value</th>
                <th className="px-4 py-3 text-left text-[10px] tracking-[0.2em] uppercase font-bold text-graphite/55">Issuers</th>
              </tr>
            </thead>
            <tbody data-testid="ladder-rungs">
              {data?.rungs.length === 0 && (
                <tr><td colSpan={5} className="p-12 text-center text-graphite/45 font-mono text-xs">NO MATURITIES YET</td></tr>
              )}
              {data?.rungs.map((r) => (
                <tr key={r.year} className="border-b border-stone/60 hover:bg-white">
                  <td className="px-4 py-3">
                    <div className="font-mono text-graphite text-base">{r.year}</div>
                    <div className="text-[10px] font-mono text-graphite/45 uppercase tracking-widest">
                      {r.years_out === 0 ? "CURRENT" : `+${r.years_out} yr`}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-right text-groen-dark">{fmtMoney(r.total_income)}</td>
                  <td className="px-4 py-3 font-mono text-right text-paars-dark">{fmtMoney(r.principal_returning)}</td>
                  <td className="px-4 py-3 font-mono text-right text-graphite/80">{fmtMoney(r.market_value)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(r.tickers).map(([t, v]) => (
                        <span key={t} className="inline-flex items-center gap-1.5 px-2 py-0.5 border border-stone bg-white font-mono text-[10px]">
                          <span className="w-2 h-2 inline-block" style={{ background: colorMap[t] }} />
                          <span className="text-graphite/80">{t}</span>
                          <span className="text-graphite/45">{fmtMoney(v)}</span>
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, accent }) {
  return (
    <div className="bg-champagne p-5">
      <div className="text-[10px] tracking-[0.25em] uppercase text-graphite/55 font-bold">{label}</div>
      <div className={`text-2xl font-mono mt-2 ${accent || "text-graphite"}`}>{value}</div>
      {sub && <div className="text-[11px] font-mono text-graphite/45 mt-1">{sub}</div>}
    </div>
  );
}
